import React from 'react';
import { useTranslation } from 'react-i18next';
import { localizeClassItem } from '../../config/classUiConfig';
import './CertificatesSection.css';

const CertificatesSection = ({ certificates }) => {
    const { t, i18n } = useTranslation('profile');
    const dateLocale = i18n.language?.startsWith('es') ? 'es-ES' : 'en-US';

    const formatTime = (timeStr) => {
        if (!timeStr) return '';
        try {
            const [hour, minute] = timeStr.split(':');
            const date = new Date();
            date.setHours(Number(hour), Number(minute), 0, 0);
            return date.toLocaleTimeString(dateLocale, { hour: 'numeric', minute: '2-digit' });
        } catch (error) {
            console.error('Error formatting time:', timeStr, error);
            return timeStr || '';
        }
    };

    const isCPRClass = (className) => {
        if (!className) return false;
        const classNameLower = className.toLowerCase();
        return classNameLower.includes('cpr') || classNameLower.includes('first aid');
    };

    const getLocalizedClassName = (name) => {
        if (!name) return name;
        return localizeClassItem({ title: name }, i18n.language).title;
    };

    const safeCertificates = certificates || [];

    return (
        <div className="certificates-section">
            <div className="section-header">
                <h2>{t('certificates.title')}</h2>
            </div>

            {safeCertificates.length > 0 ? (
                <div className="certificates-grid">
                    {safeCertificates.map(certificate => {
                        const className = certificate.class_name || certificate.certificate_name;
                        const isCPR = isCPRClass(className);
                        return (
                            <div key={certificate.id} className="certificate-card">
                                <div className="certificate-info">
                                    <h3>{getLocalizedClassName(className)}</h3>
                                    {certificate.session_date && (
                                        <p className="session-info">
                                            <i className="fas fa-calendar-alt"></i>
                                            {t('certificates.session')} {new Date(certificate.session_date).toLocaleDateString(dateLocale)}
                                            {certificate.start_time && certificate.end_time && (
                                                <span className="session-time">
                                                    {' '}({formatTime(certificate.start_time)} - {formatTime(certificate.end_time)})
                                                </span>
                                            )}
                                        </p>
                                    )}
                                    {certificate.expiration_date && (
                                        <p className="expiration-info">
                                            <i className="fas fa-clock"></i>
                                            {t('certificates.expires')} {new Date(certificate.expiration_date).toLocaleDateString(dateLocale)}
                                            {new Date(certificate.expiration_date) < new Date() && (
                                                <span className="expired-badge">{t('certificates.expired')}</span>
                                            )}
                                        </p>
                                    )}
                                    <p className="issue-date">
                                        <i className="fas fa-calendar-check"></i>
                                        {t('certificates.issued')} {new Date(certificate.created_at).toLocaleDateString(dateLocale)}
                                    </p>
                                </div>
                                {!isCPR && certificate.certificate_url && (
                                    <div className="certificate-actions">
                                        <a
                                            href={certificate.certificate_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="btn btn-primary"
                                        >
                                            <i className="fas fa-download"></i>
                                            {t('certificates.download')}
                                        </a>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="empty-state">
                    <i className="fas fa-certificate"></i>
                    <p>{t('certificates.emptyTitle')}</p>
                    <p className="empty-state-subtext">
                        {t('certificates.emptyDesc')}
                    </p>
                </div>
            )}
        </div>
    );
};

export default CertificatesSection;
